import { StyleDeclaration, css } from 'aphrodite';
import outy, { IOutyHandle } from 'outy';
import * as React from 'react';
import { DisclosureIcon } from '../svgs/icons/DisclosureIcon';
import { CompoundButtonType, getDisclosureIconFillColor, styleSheet } from './styles';

// Export the enum for external use
export { CompoundButtonType } from './styles';

export interface CompoundButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	/**
	 * The content of the main button
	 */
	buttonTitle: React.ReactNode;
	/**
	 * Class name for the container element
	 */
	className?: string;
	/**
	 * Class name for the main button
	 */
	contentClassName?: string;
	/**
	 * Class name for the trigger button
	 */
	optionsTriggerClassName?: string;
	/**
	 * Class name for the dropdown menu
	 */
	popupContentClassName?: string;
	/**
	 * Direction for the dropdown menu to open
	 */
	openDirection?: 'up' | 'down';
	/**
	 * Tooltip text for the trigger button
	 */
	optionsTriggerHoverText?: string;
	/**
	 * The type of the button
	 */
	kind: CompoundButtonType;
	/**
	 * Additional style declarations for the container
	 */
	styleDeclaration?: StyleDeclaration | StyleDeclaration[];
	/**
	 * Whether dropdown is disabled
	 */
	disabled?: boolean;
}

export const CompoundButton: React.FC<CompoundButtonProps> = ({
	buttonTitle,
	children,
	className = '',
	contentClassName = '',
	optionsTriggerClassName = '',
	popupContentClassName = '',
	openDirection = 'down',
	optionsTriggerHoverText,
	kind = CompoundButtonType.CtaReverse,
	styleDeclaration,
	disabled = false,
	onClick,
	...restProps
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const outsideClickHandleRef = React.useRef<IOutyHandle>(null);

	const toggleDropdown = React.useCallback(() => {
		if (!disabled) {
			setIsOpen(prev => !prev);
		}
	}, [disabled]);

	React.useEffect(() => {
		if (containerRef.current && isOpen) {
			outsideClickHandleRef.current = outy([containerRef.current], ['click', 'touchstart'], () => setIsOpen(false));
		}

		return () => {
			if (outsideClickHandleRef.current) {
				outsideClickHandleRef.current.remove();
			}
		};
	}, [isOpen]);

	const handleItemClick = (callback: () => void) => {
		return () => {
			callback();
			setIsOpen(false);
		};
	};

	const processedChildren = React.Children.map(children, child => {
		if (!React.isValidElement(child)) return child;

		const childProps = child.props;
		if (typeof childProps.onClick === 'function') {
			return React.cloneElement(child, {
				...childProps,
				onClick: handleItemClick(childProps.onClick),
			});
		}

		return child;
	});

	const fillColor = getDisclosureIconFillColor(kind);

	return (
		<div
			ref={containerRef}
			className={`${css(styleSheet.container, Array.isArray(styleDeclaration) ? styleDeclaration : [styleDeclaration])} ${className}`}
		>
			<div className={`btn-group ${css(styleSheet[`${kind}-buttonGroup`])}`}>
				<button
					className={`main-btn ${css(styleSheet.baseMainButton, styleSheet[kind])} ${contentClassName}`}
					onClick={onClick}
					disabled={disabled}
					{...restProps}
				>
					{buttonTitle}
				</button>
				<button
					className={`trigger-btn ${css(styleSheet.baseTriggerButton, styleSheet[`${kind}-triggerButton`])} ${optionsTriggerClassName}`}
					onClick={toggleDropdown}
					title={optionsTriggerHoverText}
					disabled={disabled}
					type='button'
				>
					<span style={{ position: 'relative', width: '100%', height: '100%', display: 'flex' }}>
						<DisclosureIcon
							className={css(styleSheet.chevronIcon, isOpen && styleSheet.chevronIconRotated)}
							fillColor={fillColor}
						/>
					</span>
				</button>
			</div>

			{isOpen && (
				<div
					className={`${css(
						styleSheet[`${kind}-dropdown`],
						openDirection === 'up' ? styleSheet.dropdownMenuUp : styleSheet.dropdownMenuDown
					)} ${popupContentClassName}`}
					data-testid='compound-button-dropdown'
				>
					{processedChildren}
				</div>
			)}
		</div>
	);
};
